import {ApolloClient, ApolloError, ApolloQueryResult, ServerError, gql} from "@apollo/client";
import log from "loglevel";
import {
  IServerSideDatasource,
  IServerSideGetRowsParams,
  IServerSideGetRowsRequest,
  SortModelItem
} from "@ag-grid-community/core";

import {
  SearchableSortDirection,
  SearchStopFilter,
  Stop,
  StopFilter,
  StopSortInput,
  StopTextFields
} from "../../generated/graphql";
import {Constants} from "../../constants/constants";
import {createCondition, buildQuery, makeQueryStringFilter} from "../../utils/FilterUtils";
import {toServerColorFilter, toServerRankSortAscending} from "../../utils/color-indicator/color-indicator.utils";
import {ManifestStatusType} from "../../components/settings/ColorSettings/ColorizedIndicators/types/manifestAttributes";
import {getColumnIdToFields} from "../../utils/ag-grid/AGGridUtils";
import {AgStop} from "../../components/common/types/enhanced";

type Decorator = (params: IServerSideGetRowsParams, results: AgStop[], res: ApolloQueryResult<any>) => Promise<void>;

type CreateStopsServerSideDatasourceProps = {
  client: ApolloClient<any>;
  defaultFields?: string[];
  excludeFields?: string[];
  onDatasourceFail?(error: ApolloError | undefined): void;
  decorateResults: Decorator[];
  tokenExpiry: Date | undefined;
  backendColorRules?: boolean;
};

export type GridContext = {
  globalQuery?: string;
  globalFilter?: StopFilter;
  additionalFilters?: {name: string; getter: () => StopFilter}[];
};

const CustomOperatorFields = new Map<string | undefined, string>([
  ["job.jobNumber", "matchPhrasePrefix"],
  ["address", "matchPhrasePrefix"],
  ["name", "matchPhrasePrefix"],
  ["city", "matchPhrasePrefix"],
  ["note", "matchPhrasePrefix"],
  ["podName", "matchPhrasePrefix"],
  ["driver.name", "matchPhrasePrefix"],
  ["order.customer.notes", "matchPhrasePrefix"],
  ["order.customer.name", "matchPhrasePrefix"],
  ["order.notes", "matchPhrasePrefix"],
  ["order.caller", "matchPhrasePrefix"],
  ["order.callerPhone", "matchPhrasePrefix"],
  ["order.auth", "matchPhrasePrefix"],
  ["order.alias", "matchPhrasePrefix"]
]);

const MappingFilterFields = new Map<string, string>([["order.customer.name", "order.customer.customerId"]]);

const CustomerFilterTypeFields = new Map<string, string>([["order.orderSkills.id", "setNumber"]]);

const StopSortDefault = [
  {field: "manifest.manifestDriverId", direction: "asc"},
  {field: "manifestSequence", direction: "asc"},
  {field: "jobStopId", direction: "asc"}
] as StopSortInput[];

export const createStopsServerSideDatasource = ({
  client,
  defaultFields,
  excludeFields,
  onDatasourceFail,
  decorateResults,
  tokenExpiry,
  backendColorRules
}: CreateStopsServerSideDatasourceProps): IServerSideDatasource => {
  return {
    getRows: function (params: IServerSideGetRowsParams<AgStop, GridContext>) {
      const {startRow, filterModel, sortModel}: IServerSideGetRowsRequest = params.request;
      const colIdToFields: {[key: string]: string[]} = getColumnIdToFields(
        params.columnApi.getAllGridColumns(),
        excludeFields
      );

      const {globalQuery, globalFilter} = params.context;

      let filter: SearchStopFilter = {
        stop: {
          and: [
            {
              or: [
                {manifest_manifestStatus: {match: ManifestStatusType.Active}},
                {manifest_manifestStatus: {match: ManifestStatusType.Future}},
                {manifest_manifestStatus: {match: ManifestStatusType.Inactive}}
              ]
            },
            {not: {jobStopStatus: {match: "X"}}},
            ...(params.context.additionalFilters || []).map((filter) => filter.getter()),
            ...buildStopsFilter(filterModel, backendColorRules)
          ]
        }
      };
      if (globalFilter) {
        filter = {
          stop: {and: [globalFilter, {...filter?.stop}]}
        };
      }

      if (globalQuery) {
        filter.queryString = makeQueryStringFilter(globalQuery, [
          "job.jobNumber",
          "job.routeNumber",
          "job.site.code",
          "name",
          "address",
          "city",
          "state",
          "dispatchZone",
          "note",
          "podName",
          "driver.name",
          "order.customer.notes",
          "order.customer.name",
          "order.caller",
          "order.callerPhone",
          "order.alias",
          "order.auth",
          "order.orderId",
          "order.notes"
        ]);
      }
      log.debug("StopFilter:", filter);

      let sort = [...buildSort(sortModel, colIdToFields, backendColorRules)];

      const isSortingByManifestDriverId = sort.some((item) => item.field === StopSortDefault[0].field);
      const isSortingByManifestSequence = sort.some((item) => item.field === StopSortDefault[1].field);

      if (!isSortingByManifestDriverId && !isSortingByManifestSequence) {
        sort = sort.concat(StopSortDefault);
      }
      if (isSortingByManifestDriverId) {
        sort = sort.concat([StopSortDefault[1], StopSortDefault[2]]);
      }
      if (isSortingByManifestSequence) {
        sort = sort.concat([StopSortDefault[0], StopSortDefault[2]]);
      }

      log.debug(`StopSort: ${JSON.stringify(sort)}`);

      const visibleColumnIds = Object.values(
        getColumnIdToFields(params.columnApi.getAllDisplayedColumns(), excludeFields)
      )
        .flatMap((x) => x)
        .concat(defaultFields || []);

      const gqlCols = buildQuery(visibleColumnIds);
      log.debug(`GQL Columns: ${gqlCols}`);

      const query = gql(/* GRAPHQL */ `
                    query SearchStops($filter: SearchStopFilter, $sort: [StopSortInput], $offset: Int, $limit: Int) {
                        searchStops(filter: $filter, sort: $sort, offset: $offset, limit: $limit) {
                            items {
                                ${gqlCols}
                            }
                            nextToken,
                            total
                        }
                    }
                `);

      let retry = 0;

      const getData = async () => {
        try {
          params.api.hideOverlay();
          const res = await client.query({
            query: query,
            variables: {
              offset: startRow,
              limit: params.api.paginationGetPageSize(),
              filter: filter,
              sort: sort
            }
          });

          const serverSideResults = (res.data.searchStops.items as Stop[]).map((j) => {
            return {...j} as AgStop;
          });

          if (decorateResults) {
            for await (const decorator of decorateResults) {
              await decorator(params, serverSideResults, res);
            }
          }

          params.success({
            rowData: serverSideResults,
            rowCount: res.data.searchStops.total
          });
          retry = 0;
        } catch (error) {
          handleError(error as ApolloError);
        }
      };

      const handleError = (err: ApolloError) => {
        const netError = err.networkError as ServerError;
        if (netError && netError.statusCode === 401) {
          //This will retry at the ApolloLink level
          log.debug("Cancel loading with a fake success");
          params.success({rowData: [], rowCount: 0});
          return;
        }
        if (err?.message.startsWith("Cannot return null for non-nullable type:")) {
          onDatasourceFail?.(new ApolloError({errorMessage: "BadData"}));
          params.api.showNoRowsOverlay();
          params.fail();
          return;
        }
        //Refetch if type error
        if (err.name === "TypeError") {
          log.debug("Retrying by TypeError:", err);
          getData();
          return;
        }
        if (retry === Constants.MAX_NUMBER_OF_RETRY) {
          log.error("Stops grid error", {
            error: err,
            raw: JSON.stringify(err),
            tokenExpiry: tokenExpiry,
            clientDateTime: new Date()
          });
          onDatasourceFail?.(err);
          params.fail();
          return;
        }
        retry++;
        log.debug(`Retrying ${retry} by error:`, err);
        getData();
      };
      onDatasourceFail?.(undefined);
      getData();
    }
  };
};

export const buildSort = (
  sortModel: SortModelItem[],
  colIdToFields: {[key: string]: string[]},
  backendColorRules?: boolean
) => {
  const newSortModel = [...sortModel];
  const newColIdToFields = {...colIdToFields};
  const addressSortIndex = newSortModel.findIndex((item) => item.colId === "stop.address");
  const filterAndSortByColorsIndex = newSortModel.findIndex((item) => item.colId === "indicatorColor");
  if (addressSortIndex !== -1) {
    newSortModel.splice(addressSortIndex, 0, {
      colId: "stop.zip",
      sort: newSortModel[addressSortIndex].sort
    });
    newColIdToFields["stop.zip"] = ["stop.zip"];
  }
  if (filterAndSortByColorsIndex !== -1) {
    if (backendColorRules) {
      // DP-3216: the direction is INVERTED on purpose — see toServerRankSortAscending. Rank 1 is
      // the most important colour, so ascending by rank number is descending by importance, which
      // is the reverse of what the client fallback below has always done.
      newSortModel[filterAndSortByColorsIndex] = {
        ...newSortModel[filterAndSortByColorsIndex],
        colId: "indicatorRank",
        sort: toServerRankSortAscending(newSortModel[filterAndSortByColorsIndex].sort === "asc") ? "asc" : "desc"
      };
      newColIdToFields["indicatorRank"] = ["indicatorRank"];
    } else {
      newSortModel.splice(filterAndSortByColorsIndex, 1);
    }
  }
  return newSortModel
    .filter((sm) => newColIdToFields[sm.colId] !== undefined)
    .map((sm) => {
      let sortKey: string;
      const textFields = StopTextFields;
      const sortInput = {} as StopSortInput;

      sortKey = sm.colId;
      const textFieldKey = sortKey.replaceAll(".", "_");

      if ((Object.values(textFields) as string[]).includes(textFieldKey)) {
        sortKey += ".keyword";
      }
      sortInput.field = sortKey;
      sortInput.direction = sm.sort === "asc" ? SearchableSortDirection.Asc : SearchableSortDirection.Desc;
      return sortInput;
    });
};

// DP-3118: see buildJobsFilter — `indicatorColor` reaches the server only when the
// worker stamps it (backendColorRules on); otherwise the column is client-side only.
export const buildStopsFilter = (filterModel: any, backendColorRules?: boolean): any[] => {
  const filters: any[] = [];
  if (filterModel) {
    Object.keys(filterModel)
      .filter((x) => backendColorRules || x !== "indicatorColor")
      .forEach(function (columnKey) {
        let key = columnKey;
        const modelFilter =
          columnKey === "indicatorColor" ? toServerColorFilter(filterModel[columnKey]) : filterModel[columnKey];
        if (MappingFilterFields.has(columnKey)) {
          key = MappingFilterFields.get(columnKey)!;
        }
        if (CustomerFilterTypeFields.has(columnKey)) {
          modelFilter.filterType = CustomerFilterTypeFields.get(columnKey);
        }
        const condition = createCondition(key.replaceAll(".", "_"), modelFilter, columnKey, CustomOperatorFields);
        filters.push(condition);
      });
  }
  return filters;
};
